export const LOGIN_REDIRECT_QUERY_KEY = 'redirect'

/**
 * The checkout routes.
 *
 * A 401 encountered on one of these must NOT trigger the global "your session
 * died, go to /login" redirect — that would throw away a half-finished order.
 *
 * `/configure` is the same checkout without a session, so it belongs here for a
 * stronger reason: on that route a 401 is not a dead session at all, it is the
 * ORDINARY state. Leaving it out sent an anonymous visitor to `/login` the
 * moment any background helper touched an authenticated endpoint — which the
 * websocket does on mount, so the public page redirected itself away before
 * anybody could read it. Its signed-out destination is also itself, which is
 * why `redirectToAuth` sends `/billing/checkout` here rather than to
 * `/register`.
 *
 * The comment that used to sit here described an "onboarding modal" that was
 * never built. `/configure` is what that idea became.
 */
export const AUTH_OPTIONAL_PATH_PREFIXES = [
  '/configure',
  '/billing/checkout',
  '/vpn-gateway/checkout',
]

export function isAuthOptionalPath(path: string | undefined | null): boolean {
  if (!path) return false
  return AUTH_OPTIONAL_PATH_PREFIXES.some(prefix => path.startsWith(prefix))
}

/**
 * API paths that ANSWER a credential rather than consume a session.
 *
 * A 401 from one of these means "that password/code is wrong", not "your
 * session expired" — so the global axios interceptor must not treat it as a
 * lost session and navigate to /login. It used to: mistyping the emailed
 * two-factor code on the register page threw the customer off the page, taking
 * the challenge with it, seconds after their account had been created.
 */
export const AUTH_ANSWER_PATHS = [
  'whmcs/login',
  'whmcs/login/verify-two-factor',
  'whmcs/login/resend-two-factor',
  'whmcs/register',
  'whmcs/register/check-email',
  'whmcs/password/reset',
  'whmcs/sso/exchange',
  'sso/exchange',
  'whmcs/social/handoff/exchange',
  'whmcs/social/complete-registration',
  'whmcs/social/select-account',
]

export function isAuthAnswerPath(url: string | undefined | null): boolean {
  if (!url) return false
  const path = url.split('?')[0]!.replace(/^.*\/api\/mw\//, '').replace(/^\/+/, '')
  return AUTH_ANSWER_PATHS.includes(path)
}

export function resolveSafeRedirectTarget(value: unknown, fallback = '/'): string {
  const candidate = Array.isArray(value) ? value[0] : value

  if (typeof candidate !== 'string' || candidate.length === 0) {
    return fallback
  }

  // Must be a plain internal path. `//` is protocol-relative, and `/\` is
  // treated as `//` by browsers that normalise backslashes — both leave the
  // origin, which is the whole thing this function exists to prevent.
  if (!candidate.startsWith('/') || /^\/[/\\]/.test(candidate)) {
    return fallback
  }

  return candidate
}
